import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    TextField,
    CircularProgress,
    Button,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CropIcon from '@mui/icons-material/Crop';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useSelector } from '../../../redux/store';
import EventsApi, { PinQuestion } from '../../../api/eventsApi/eventsApi';
import { getCategoryIcon } from './ChecklistPage';
import FormattedLlmText from './FormattedLlmText';
import { formatSectorDisplay } from '../facilitySectors';
import CoverPhotoModal from '../../home/components/coverPhotoModal';
import {
    COVER_EXPORT_ASPECT_RATIO,
    COVER_PREVIEW_MAX_WIDTH_PX,
} from '../coverPhotoSpec';

interface CategoryStats {
    name: string;
    answered: number;
    total: number;
    icon: React.ReactNode;
}

const EVALUATION_LABEL = 'sonuç ve değerlendirme';
const DEFAULT_QUESTION_TYPE_ID = 'd5d3dd45-0a2e-457d-933b-c66a3853469f';

const normalizeText = (value?: string | null) =>
    (value ?? '')
        .toLocaleLowerCase('tr-TR')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

const isEvaluationQuestion = (q?: { text?: string | null } | null) =>
    normalizeText(q?.text) === normalizeText(EVALUATION_LABEL);

const resolveLatestCoverPhotoUrl = async (attachments: any[] | undefined): Promise<string | null> => {
    const coverCandidates = [...(attachments ?? [])]
        .filter((file: any) => {
            const fileName = String(file?.fileName ?? file?.FileName ?? '').toLowerCase();
            return fileName.startsWith('cover') &&
                (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png'));
        })
        .reverse();

    for (const file of coverCandidates) {
        const fileId = file?.id ?? file?.Id;
        if (!fileId) continue;
        try {
            const blob = await EventsApi.getFileAttachment(fileId);
            return URL.createObjectURL(blob);
        } catch (error) {
            console.warn('Skipping unavailable cover photo attachment:', fileId, error);
        }
    }

    return null;
};

const AuditSummaryPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const storeUserId = useSelector((state) => state.auth.id) ?? '';
    const localStorageUserId = useMemo(
        () => (typeof window !== 'undefined' ? localStorage.getItem('userId') ?? '' : ''),
        []
    );

    const [loading, setLoading] = useState(true);
    const [isFinishing, setIsFinishing] = useState(false);
    const [pinName, setPinName] = useState('');
    const [sector, setSector] = useState('');

    // Stats
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [answeredCount, setAnsweredCount] = useState(0);
    const [bulguCount, setBulguCount] = useState(0);
    const [skippedCount, setSkippedCount] = useState(0);
    const [uygunCount, setUygunCount] = useState(0);

    // Cover photo
    const [coverPhotoUrl, setCoverPhotoUrl] = useState<string | null>(null);
    const [coverModalOpen, setCoverModalOpen] = useState(false);

    // Evaluation
    const [evaluationText, setEvaluationText] = useState('');
    const [evaluationQuestion, setEvaluationQuestion] = useState<PinQuestion | null>(null);
    const [evaluationAnswerId, setEvaluationAnswerId] = useState<string | null>(null);
    const [isSavingEval, setIsSavingEval] = useState(false);
    const [isGeneratingInsight, setIsGeneratingInsight] = useState(false);
    const [isEvalDirty, setIsEvalDirty] = useState(false);
    const [evalSaveStatus, setEvalSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
    const [evalSaveErrorText, setEvalSaveErrorText] = useState('');

    // Unanswered questions & category stats
    const [unansweredQuestions, setUnansweredQuestions] = useState<{ category: string; text: string }[]>([]);
    const [categoryStats, setCategoryStats] = useState<CategoryStats[]>([]);
    const [showAllUnanswered, setShowAllUnanswered] = useState(false);

    // Detailed findings for LLM context
    const findingsDetailRef = useRef<{ category: string; question: string; findings: string; proposedSolutions: string }[]>([]);

    const resolveEffectiveUserId = useCallback(async (): Promise<string> => {
        if (storeUserId) return storeUserId;
        if (localStorageUserId) return localStorageUserId;
        try {
            const user: any = await EventsApi.getUser();
            return user?.id ?? '';
        } catch {
            return '';
        }
    }, [storeUserId, localStorageUserId]);

    const ensureEvaluationQuestionExists = useCallback(async () => {
        if (!id) return null;

        const initial = await EventsApi.getPinQuestionsAndAnswers(id);
        const existing = initial.questions.find(q => isEvaluationQuestion(q));
        if (existing) return existing;

        const effectiveUserId = await resolveEffectiveUserId();
        if (!effectiveUserId) return null;

        const sampleQuestion = initial.questions[0];
        const questionTypeId =
            sampleQuestion?.questionCategory?.questionType?.id || DEFAULT_QUESTION_TYPE_ID;

        const createdCategory: any = await EventsApi.saveQuestionCategory({
            name: 'SONUÇ VE DEĞERLENDİRME',
            userId: effectiveUserId,
            questionTypeId,
            pinId: id,
        } as any);

        const categoryId = createdCategory?.id ?? createdCategory?.Id ?? null;
        if (!categoryId) return null;

        await EventsApi.saveQuestion({
            text: 'Sonuç ve Değerlendirme',
            userId: effectiveUserId,
            questionCategoryId: categoryId,
            pinId: id,
        });

        const refreshed = await EventsApi.getPinQuestionsAndAnswers(id);
        return refreshed.questions.find(q => isEvaluationQuestion(q)) ?? null;
    }, [id, resolveEffectiveUserId]);

    const loadData = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const [pinInfo, qaData] = await Promise.all([
                EventsApi.getPinInfo(id),
                EventsApi.getPinQuestionsAndAnswers(id),
            ]);

            setPinName((pinInfo as any).name ?? (pinInfo as any).Name ?? '');
            const s = (pinInfo as any).sector ?? (pinInfo as any).Sector ?? '';
            const sc =
                (pinInfo as any).sectorCustom ?? (pinInfo as any).SectorCustom ?? null;
            setSector(formatSectorDisplay(s, sc));

            // Cover photo
            const attachments = (pinInfo as any).fileAttachments ?? (pinInfo as any).FileAttachments ?? [];
            const resolvedCoverUrl = await resolveLatestCoverPhotoUrl(attachments);
            setCoverPhotoUrl(resolvedCoverUrl);

            // Process questions
            const questions = qaData.questions.filter(q => !isEvaluationQuestion(q));
            let evalQ = qaData.questions.find(q => isEvaluationQuestion(q));
            if (!evalQ) {
                evalQ = await ensureEvaluationQuestionExists();
            }
            setEvaluationQuestion(evalQ ?? null);
            if (evalQ?.answers?.length) {
                setEvaluationText(evalQ.answers[0].description ?? '');
                setEvaluationAnswerId(evalQ.answers[0].id ?? null);
            } else {
                setEvaluationText('');
                setEvaluationAnswerId(null);
            }
            setIsEvalDirty(false);
            setEvalSaveStatus('idle');
            setEvalSaveErrorText('');

            const total = questions.length;
            let answered = 0;
            let bulgular = 0;
            let skipped = 0;
            let uygun = 0;
            const unanswered: { category: string; text: string }[] = [];
            const catMap = new Map<string, { answered: number; total: number; name: string }>();
            const detailedFindings: typeof findingsDetailRef.current = [];

            for (const q of questions) {
                const catName = q.questionCategory?.name ?? 'Diğer';
                if (!catMap.has(catName)) catMap.set(catName, { answered: 0, total: 0, name: catName });
                const cat = catMap.get(catName)!;
                cat.total++;

                const ans = q.answers?.[0];
                if (!ans) {
                    unanswered.push({ category: catName, text: q.text });
                    continue;
                }

                const yesNo = ans.yesNo;
                const desc = ans.description ?? '';

                if (desc === 'gerek_yok' || desc === 'Gerek yok') {
                    skipped++;
                    answered++;
                    cat.answered++;
                } else if (yesNo === true) {
                    uygun++;
                    answered++;
                    cat.answered++;
                } else if (yesNo === false) {
                    answered++;
                    cat.answered++;
                    const findings = ans.findings?.split('\n').filter(Boolean) ?? [];
                    bulgular += Math.max(findings.length, 1);
                    if (ans.findings?.trim() || ans.proposedSolutions?.trim()) {
                        detailedFindings.push({
                            category: catName,
                            question: q.text,
                            findings: ans.findings ?? '',
                            proposedSolutions: ans.proposedSolutions ?? '',
                        });
                    }
                } else {
                    unanswered.push({ category: catName, text: q.text });
                }
            }
            findingsDetailRef.current = detailedFindings;

            setTotalQuestions(total);
            setAnsweredCount(answered);
            setBulguCount(bulgular);
            setSkippedCount(skipped);
            setUygunCount(uygun);
            setUnansweredQuestions(unanswered);
            setCategoryStats(
                Array.from(catMap.values()).map(c => ({
                    ...c,
                    icon: getCategoryIcon(c.name),
                }))
            );
        } catch (e) {
            console.error('Audit summary load error:', e);
        } finally {
            setLoading(false);
        }
    }, [id, ensureEvaluationQuestionExists]);

    useEffect(() => { loadData(); }, [loadData]);

    const completionPercent = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;
    const unansweredPercent = totalQuestions > 0 ? 100 - completionPercent : 0;

    const effectiveCoverUserId = storeUserId || localStorageUserId;

    // Save evaluation
    const handleSaveEvaluation = useCallback(async (text?: string): Promise<boolean> => {
        const toSave = (text ?? evaluationText).trim();
        if (!id || !toSave) return false;
        setIsSavingEval(true);
        setEvalSaveStatus('idle');
        setEvalSaveErrorText('');
        try {
            const effectiveUserId = await resolveEffectiveUserId();
            if (!effectiveUserId) {
                setEvalSaveStatus('error');
                setEvalSaveErrorText('Kullanıcı bilgisi alınamadı.');
                return false;
            }

            const targetEvaluationQuestion = await ensureEvaluationQuestionExists();
            if (!targetEvaluationQuestion) {
                setEvalSaveStatus('error');
                setEvalSaveErrorText('Sonuç ve Değerlendirme sorusu oluşturulamadı.');
                return false;
            }
            setEvaluationQuestion(targetEvaluationQuestion);

            const existingId = evaluationAnswerId ?? targetEvaluationQuestion.answers?.[0]?.id ?? null;
            if (existingId) {
                const formData = new FormData();
                formData.append('Id', existingId);
                formData.append('QuestionId', targetEvaluationQuestion.id);
                formData.append('UserId', effectiveUserId);
                formData.append('YesNo', 'true');
                formData.append('Findings', '');
                formData.append('PinId', id);
                formData.append('ProposedSolutions', '');
                formData.append('Description', toSave);
                await EventsApi.updateQuestionAnswer(formData);
            } else {
                const res: any = await EventsApi.saveQuestionAnswers({
                    questionId: targetEvaluationQuestion.id,
                    userId: effectiveUserId,
                    yesNo: true,
                    findings: '',
                    pinId: id,
                    proposedSolutions: '',
                    description: toSave,
                });
                const newId = res?.id ?? res?.Id ?? res?.data?.id ?? res?.data?.Id ?? null;
                if (newId) setEvaluationAnswerId(String(newId));
            }
            EventsApi.clearCacheItem(`pin_full_report_${id}`);
            EventsApi.clearCacheItem(`pin_info_${id}`);
            setIsEvalDirty(false);
            setEvalSaveStatus('saved');
            return true;
        } catch (err) {
            console.error('Evaluation save error:', err);
            setEvalSaveStatus('error');
            setEvalSaveErrorText((err as any)?.response?.data?.detail || (err as any)?.response?.data?.message || 'Sunucu kaydı başarısız oldu.');
            return false;
        } finally {
            setIsSavingEval(false);
        }
    }, [id, evaluationText, evaluationAnswerId, resolveEffectiveUserId, ensureEvaluationQuestionExists]);

    // Generate evaluation with Umay
    const handleGenerateInsight = async () => {
        if (!id) return;
        setIsGeneratingInsight(true);
        try {
            const details = findingsDetailRef.current;
            const findingsSummaryParts: string[] = [];
            const recommendationParts: string[] = [];
            const criticalParts: string[] = [];
            const categoryDistribution: string[] = [];

            if (details.length > 0) {
                const byCategory = new Map<string, typeof details>();
                for (const d of details) {
                    if (!byCategory.has(d.category)) byCategory.set(d.category, []);
                    byCategory.get(d.category)!.push(d);
                }
                for (const [cat, items] of byCategory) {
                    categoryDistribution.push(`${cat}: ${items.length} bulgu`);
                    findingsSummaryParts.push(`\n${cat}:`);
                    for (const item of items) {
                        findingsSummaryParts.push(`Soru: ${item.question}`);
                        if (item.findings) findingsSummaryParts.push(`Tespit: ${item.findings}`);
                        if (item.proposedSolutions) {
                            recommendationParts.push(`${item.question}: ${item.proposedSolutions}`);
                        }
                    }
                }
            }

            const tespitText = [
                `Tesis bilgisi: ${pinName}${sector ? ` | Sektör: ${sector}` : ''}`,
                `Toplam denetim puanı: ${totalQuestions} soru, ${answeredCount} cevaplanan, ${uygunCount} uygun, ${bulguCount} bulgu, ${skippedCount} gerek yok`,
                `Tamamlanma oranı: %${totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0}`,
                categoryDistribution.length > 0
                    ? `\nBulguların kategori bazlı dağılımı:\n${categoryDistribution.join('\n')}`
                    : '',
                details.length > 0
                    ? `\nTüm bulguların özeti:${findingsSummaryParts.join('\n')}`
                    : 'Detaylı bulgu bilgisi mevcut değil.',
                recommendationParts.length > 0
                    ? `\nÜretilen önerilerin özeti:\n${recommendationParts.join('\n')}`
                    : '',
            ].filter(Boolean).join('\n');

            const res = await EventsApi.generateInsight({
                question: `${pinName} tesisi saha denetimi sonuç ve değerlendirme raporu`,
                tespit: tespitText,
                promptType: 'evaluation',
                facilityContext: sector || undefined,
            });
            if (res.insight) {
                setEvaluationText(res.insight);
                setIsEvalDirty(true);
                await handleSaveEvaluation(res.insight);
            }
        } catch (err) {
            console.error('Insight generation error:', err);
        } finally {
            setIsGeneratingInsight(false);
        }
    };

    // Finish - create report and open preview
    const handleFinish = useCallback(async () => {
        if (!id) return;
        // Open report page immediately on click to avoid popup blockers.
        window.open(`/report-view/${id}`, '_blank');
        if (evaluationText.trim()) {
            await handleSaveEvaluation();
        }
        setIsFinishing(true);
        try {
            await EventsApi.refreshPinFullReport(id);

            EventsApi.clearCacheItem(`pin_report_${id}`);

            const end = new Date();
            const start = new Date();
            start.setFullYear(end.getFullYear() - 1);
            const toISO = (d: Date) => d.toISOString().split('T')[0];
            try {
                await EventsApi.getPinReport(id, toISO(start), toISO(end));
            } catch (e) {
                console.warn('Rapor kaydedilirken hata:', e);
            }

            EventsApi.clearCacheItem('pins');
            EventsApi.clearCacheItem(`pin_info_${id}`);
            EventsApi.clearCacheItem(`pin_full_report_${id}`);

            navigate(`/reports/${id}`);
        } catch (error) {
            console.error('Rapor oluşturulurken hata:', error);
            navigate(`/reports/${id}`);
        } finally {
            setIsFinishing(false);
        }
    }, [id, navigate, evaluationText, handleSaveEvaluation]);

    const handleBack = () => navigate(`/reports/${id}/checklist`);

    const today = new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

    if (!id) return null;

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    const visibleUnanswered = showAllUnanswered ? unansweredQuestions : unansweredQuestions.slice(0, 5);
    const hiddenCount = unansweredQuestions.length - 5;

    return (
        <Box sx={{ 
            height: '100vh', 
            display: 'flex', flexDirection: 'column', 
            bgcolor: '#F2F7F9', overflow: 'hidden' 
        }}>
            {/* Header */}
            <Box sx={{ bgcolor: '#0D3D4A', color: 'white', px: 3, pt: 2, pb: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    component="button" onClick={handleBack}
                    sx={{
                        width: 40, height: 40, borderRadius: 0, border: 'none',
                        bgcolor: 'rgba(255,255,255,0.12)', color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                    <Typography variant="overline" sx={{ opacity: 0.6, letterSpacing: 2 }}>ÖZET</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Özet</Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pt: 3, pb: 2 }}>
                {/* Denetim Özeti Title */}
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0D3D4A', fontFamily: 'Inter, sans-serif' }}>
                    Denetim Özeti
                </Typography>
                <Typography variant="body2" sx={{ color: '#5A7A84', mb: 2.5 }}>
                    {pinName}{sector ? ` · ${sector}` : ''} · {today}
                </Typography>

                {/* Stats Grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mb: 3 }}>
                    {[
                        { value: String(totalQuestions), label: 'TOPLAM SORU', color: '#0D3D4A' },
                        { value: String(answeredCount), label: 'CEVAPLANAN', color: '#1FA35C' },
                        { value: String(bulguCount), label: 'BULGU', color: '#D93B2B' },
                        { value: String(uygunCount), label: 'UYGUN', color: '#1FA35C' },
                        { value: String(skippedCount), label: 'GEREK YOK', color: '#E8A800' },
                        { value: `%${completionPercent}`, label: 'TAMAMLANMA', color: '#0D3D4A' },
                    ].map((stat) => (
                        <Paper
                            key={stat.label} elevation={0}
                            sx={{
                                p: 1.5, borderRadius: 2, border: '1px solid #C8DDE4',
                                textAlign: 'center', bgcolor: 'white',
                            }}
                        >
                            <Typography sx={{ fontSize: 24, fontWeight: 800, color: stat.color, fontFamily: 'Inter, sans-serif' }}>
                                {stat.value}
                            </Typography>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#5A7A84', letterSpacing: 0.5 }}>
                                {stat.label}
                            </Typography>
                        </Paper>
                    ))}
                </Box>

                {/* Kapak Fotoğrafı — kırpma/düzenleme CoverPhotoModal ile */}
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0D3D4A', mb: 1, fontFamily: 'Inter, sans-serif' }}>
                    Kapak Fotoğrafı
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 1.5,
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#5A7A84', flex: '1 1 180px', minWidth: 0 }}>
                        Rapor kapak görselini yükledikten sonra alan seçimi ve kırpma yapabilirsiniz (rapor oranı: 1200x630).
                    </Typography>
                    {coverPhotoUrl && effectiveCoverUserId ? (
                        <Button
                            variant="outlined"
                            size="medium"
                            startIcon={<CropIcon sx={{ color: '#0D3D4A', fill: 'currentColor' }} />}
                            onClick={(e) => {
                                e.stopPropagation();
                                setCoverModalOpen(true);
                            }}
                            sx={{
                                flexShrink: 0,
                                alignSelf: { xs: 'flex-start', sm: 'flex-start' },
                                backgroundColor: '#ffffff',
                                borderWidth: 2,
                                borderStyle: 'solid',
                                borderColor: '#0D3D4A',
                                color: '#0D3D4A',
                                fontWeight: 700,
                                fontSize: 14,
                                py: 1,
                                px: 2,
                                minHeight: 44,
                                borderRadius: 2,
                                textTransform: 'none',
                                '& .MuiButton-startIcon .MuiSvgIcon-root': {
                                    color: '#0D3D4A',
                                    fill: 'currentColor',
                                },
                                '&:hover': {
                                    borderColor: '#134F60',
                                    color: '#134F60',
                                    backgroundColor: 'rgba(13, 61, 74, 0.08)',
                                    '& .MuiButton-startIcon .MuiSvgIcon-root': {
                                        color: '#134F60',
                                    },
                                },
                            }}
                        >
                            Düzenle / kırp
                        </Button>
                    ) : null}
                </Box>
                <Paper
                    elevation={0}
                    onClick={() => {
                        if (!effectiveCoverUserId) return;
                        setCoverModalOpen(true);
                    }}
                    sx={{
                        borderRadius: 3, border: '1.5px dashed #C8DDE4', bgcolor: 'white',
                        p: 3, mb: 3, cursor: effectiveCoverUserId ? 'pointer' : 'not-allowed',
                        textAlign: 'center',
                        minHeight: 120, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        '&:hover': effectiveCoverUserId ? { borderColor: '#0D3D4A', bgcolor: '#EDF4F6' } : {},
                        overflow: 'hidden', position: 'relative',
                        opacity: effectiveCoverUserId ? 1 : 0.7,
                    }}
                >
                    {coverPhotoUrl ? (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: COVER_PREVIEW_MAX_WIDTH_PX,
                                    aspectRatio: COVER_EXPORT_ASPECT_RATIO,
                                    bgcolor: '#000',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    border: '1px solid #C8DDE4',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={coverPhotoUrl}
                                    alt="Kapak"
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: COVER_PREVIEW_MAX_WIDTH_PX,
                                    aspectRatio: COVER_EXPORT_ASPECT_RATIO,
                                    border: '2px dashed #C8DDE4',
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 2,
                                    bgcolor: '#F9FCFD',
                                }}
                            >
                                <PhotoCameraIcon sx={{ fontSize: 36, color: '#5A7A84', mb: 1 }} />
                                <Typography sx={{ fontWeight: 600, color: '#0D3D4A' }}>Kapak fotoğrafı ekle</Typography>
                                <Typography sx={{ fontSize: 12, color: '#5A7A84' }}>
                                    Tesis genel görünümü önerilir · tıklayarak seçim ve kırpma
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Paper>
                {!effectiveCoverUserId ? (
                    <Typography variant="caption" sx={{ color: '#D93B2B', display: 'block', mb: 2 }}>
                        Kapak yüklemek için oturum bilgisi gerekli. Sayfayı yenileyip tekrar deneyin.
                    </Typography>
                ) : null}
                <CoverPhotoModal
                    open={coverModalOpen}
                    onClose={() => setCoverModalOpen(false)}
                    pinId={id}
                    userId={effectiveCoverUserId}
                    onPhotoUploaded={() => {
                        setCoverModalOpen(false);
                        void loadData();
                    }}
                />

                {/* Sonuç ve Değerlendirme */}
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0D3D4A', mb: 1.5, fontFamily: 'Inter, sans-serif' }}>
                    Sonuç ve Değerlendirme
                </Typography>
                <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #C8DDE4', bgcolor: 'white', p: 2, mb: 0.5 }}>
                    <TextField
                        fullWidth multiline minRows={4} variant="standard"
                        value={evaluationText}
                        onChange={(e) => {
                            setEvaluationText(e.target.value);
                            setIsEvalDirty(true);
                            setEvalSaveStatus('idle');
                        }}
                        onBlur={() => handleSaveEvaluation()}
                        placeholder="Denetim genel değerlendirmesi, öne çıkan riskler ve öncelikli aksiyon önerileri..."
                        InputProps={{ disableUnderline: true }}
                        sx={{ '& .MuiInputBase-input': { fontSize: 14 } }}
                    />
                </Paper>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.5, px: 0.5 }}>
                    <Typography sx={{ fontSize: 12, color: '#5A7A84', fontWeight: 600 }}>
                        {evaluationText.length} karakter
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Box
                        component="button"
                        onClick={() => handleSaveEvaluation()}
                        disabled={isSavingEval || isGeneratingInsight || !evaluationText.trim() || !isEvalDirty}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 2.5, py: 1.2, borderRadius: 2,
                            border: '1px solid #0D3D4A', bgcolor: 'white', color: '#0D3D4A',
                            fontWeight: 700, fontSize: 14, cursor: 'pointer',
                            '&:hover': { bgcolor: '#EDF4F6' },
                            '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
                        }}
                    >
                        {isSavingEval ? <CircularProgress size={16} sx={{ color: '#0D3D4A' }} /> : null}
                        {isSavingEval ? 'Kaydediliyor...' : 'Kaydet'}
                    </Box>
                    <Box
                        component="button"
                        onClick={handleGenerateInsight}
                        disabled={isGeneratingInsight || isSavingEval}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 2.5, py: 1.2, borderRadius: 2,
                            border: 'none', bgcolor: '#0D3D4A', color: 'white',
                            fontWeight: 700, fontSize: 14, cursor: 'pointer',
                            '&:hover': { bgcolor: '#134F60' },
                            '&:disabled': { opacity: 0.6, cursor: 'wait' },
                        }}
                    >
                        {isGeneratingInsight ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <AutoAwesomeIcon sx={{ fontSize: 18 }} />}
                        Umay ile Oluştur
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3, minHeight: 18 }}>
                    {evalSaveStatus === 'saved' ? (
                        <Typography sx={{ fontSize: 12, color: '#1FA35C', fontWeight: 700 }}>
                            Kaydedildi
                        </Typography>
                    ) : evalSaveStatus === 'error' ? (
                        <Typography sx={{ fontSize: 12, color: '#D93B2B', fontWeight: 700 }}>
                            Kaydetme başarısız{evalSaveErrorText ? `: ${evalSaveErrorText}` : ', tekrar deneyin'}
                        </Typography>
                    ) : (
                        <Box />
                    )}
                </Box>

                {evaluationText.trim() ? (
                    <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0D3D4A', mb: 1 }}>
                            Biçimlendirilmiş Önizleme
                        </Typography>
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                border: '1px solid #C8DDE4',
                                bgcolor: '#F9FCFD',
                                p: 2,
                                mb: 3,
                            }}
                        >
                            <FormattedLlmText text={evaluationText} />
                        </Paper>
                    </>
                ) : null}

            </Box>

            {/* Footer */}
            <Box
                sx={{
                    bgcolor: '#F2F7F9', borderTop: '1px solid #C8DDE4',
                    p: 2, display: 'flex', gap: 2, zIndex: 40,
                }}
            >
                <Box
                    component="button" onClick={handleBack}
                    sx={{
                        minWidth: 56, minHeight: 56,
                        border: '2px solid #C8DDE4', borderRadius: '16px',
                        color: '#0D3D4A', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', bgcolor: 'white',
                        '&:hover': { borderColor: '#0D3D4A', bgcolor: 'rgba(13,61,74,0.04)' },
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box
                    component="button"
                    onClick={handleFinish}
                    disabled={isFinishing}
                    sx={{
                        flex: 1, minHeight: 56, borderRadius: '16px', border: 'none',
                        bgcolor: isFinishing ? '#134F60' : '#0D3D4A',
                        color: 'white', fontWeight: 700, fontSize: 17,
                        cursor: isFinishing ? 'wait' : 'pointer',
                        fontFamily: 'Inter, sans-serif',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
                        '&:hover': { bgcolor: '#134F60' },
                    }}
                >
                    {isFinishing ? (
                        <>
                            <CircularProgress size={20} sx={{ color: 'white' }} />
                            Rapor Oluşturuluyor...
                        </>
                    ) : (
                        <>
                            <DescriptionIcon sx={{ fontSize: 20 }} />
                            Rapor Oluştur
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default AuditSummaryPage;
